import React from 'react';
import Panel from './../../../common/layout/Panel'
import SmsLogDetails from './smslogdetails'
import DatePicker from 'react-datepicker';
import { useState, useEffect } from 'react';
import { getList, create } from '../../../common/v4/BackendProvider';
import Table from '../../../common/Table';
import SelectContainer from '../../../common/SelectContainer';
import AsyncSelectContainer from '../../../common/AsyncSelectContainer';
import DrawerPane from '../../../common/v4/DataTable/DrawerPane';
import Pagination from '../../../common/v4/DataTable/Pagination';
import { toast, ToastContainer } from 'react-toastify';
import config from '../../../../config';

const SmsLog = (props) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRows, setTotalRows] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const today = new Date();
    const [d1, setD1] = useState(new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0));
    const [d2, setD2] = useState(new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59));
    
    // Filter states
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedOriginatingCarrier, setSelectedOriginatingCarrier] = useState(null);
    const [selectedTerminatingCarrier, setSelectedTerminatingCarrier] = useState(null);
    const [selectedDestination, setSelectedDestination] = useState(null);
    const [selectedDirection, setSelectedDirection] = useState(null);
    const [sourceNumber, setSourceNumber] = useState('');
    const [destinationNumber, setDestinationNumber] = useState('');

    const [originatingCarrierOptions, setOriginatingCarrierOptions] = useState([]);
    const [terminatingCarrierOptions, setTerminatingCarrierOptions] = useState([]);
    const [destinationOptions, setDestinationOptions] = useState([]);
    const [directionOptions, setDirectionOptions] = useState([]);

    // Drawer state
    const [drawerIsOpen, setDrawerIsOpen] = useState(false);
    const [drawerId, setDrawerId] = useState(null);

    // CSV Export state
    const [csvExportLoading, setCsvExportLoading] = useState(false);
    const [csvExportStatus, setCsvExportStatus] = useState(null);
    const [csvExportTaskId, setCsvExportTaskId] = useState(null);
    const [csvExportProgress, setCsvExportProgress] = useState(0);
    const [csvDownloadUrl, setCsvDownloadUrl] = useState(null);

    // Function to load clients dynamically based on search term
    const loadClients = async (inputValue) => {
        try {
            // If no input value, load initial results (first 50 clients)
            const params = inputValue ? [
                { key: 'search', value: inputValue },
                { key: 'limit', value: 50 }
            ] : [
                { key: 'limit', value: 50 }
            ];
            
            const response = await getList('/admin/clients/clients', params);
            
            if (response.data?.result === 'OK') {
                // Convert to array format with value and label
                const clients = response.data.data.map(client => ({
                    value: client.ATRCLIENTID,
                    label: `${client.ATRACCTNAME} (${client.ATRCLIENTIDNUMBER})`
                }));
                return clients;
            }
            return [];
        } catch (error) {
            console.error('Error loading clients:', error);
            return [];
        }
    };

    const fetchData = () => {
        setLoading(true);
        const params = [
            {key: 'search', value: ''},
            {key: 'start_time_from', value: d1.toISOString()},
            {key: 'start_time_to', value: d2.toISOString()},
            {key: 'page', value: currentPage},
        ];

        // Add filter parameters if selected
        if (selectedClient) params.push({key: 'client_id', value: selectedClient});
        if (selectedOriginatingCarrier) params.push({key: 'originating_carrier_id', value: selectedOriginatingCarrier});
        if (selectedTerminatingCarrier) params.push({key: 'terminating_carrier_id', value: selectedTerminatingCarrier});
        if (selectedDestination) params.push({key: 'destination', value: selectedDestination});
        if (selectedDirection) params.push({key: 'direction', value: selectedDirection});
        if (sourceNumber) params.push({key: 'src_address', value: sourceNumber});
        if (destinationNumber) params.push({key: 'dest_address', value: destinationNumber});

        getList('/admin/reports/sms/smslog', params)
            .then(result => {
                setData(result.data.data);
                setTotalRows(result.data.count || 0);
                setRowsPerPage(result.data.rowsPerPage || 50);
                setOriginatingCarrierOptions(result.data?.metadata?.originating_carrier_id?.values || []);
                setTerminatingCarrierOptions(result.data?.metadata?.terminating_carrier_id?.values || []);
                setDestinationOptions(result.data?.metadata?.destination?.values || []);
                setDirectionOptions(result.data?.metadata?.direction?.values || []);
            })
            .catch(error => console.error('Error:', error))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchData();
    }, [currentPage]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleDateRangeFromChange = (value) => {
        setD1(value);
        setCurrentPage(1);
        // Auto-fetch when date changes
        setTimeout(() => fetchData(), 100);
    };

    const handleDateRangeToChange = (value) => {
        setD2(value);
        setCurrentPage(1);
        // Auto-fetch when date changes
        setTimeout(() => fetchData(), 100);
    };

    const handleSearch = () => {
        fetchData();
    };

    const handleFilterChange = (filterType, value) => {
        switch (filterType) {
            case 'client':
                setSelectedClient(value);
                break;
            case 'originatingCarrier':
                setSelectedOriginatingCarrier(value);
                break;
            case 'terminatingCarrier':
                setSelectedTerminatingCarrier(value);
                break;
            case 'destination':
                setSelectedDestination(value);
                break;
            case 'direction':
                setSelectedDirection(value);
                break;
        }
        setCurrentPage(1);
        // Auto-fetch when filter changes
        setTimeout(() => fetchData(), 100);
    };

    const handleTextFilterChange = (filterType, value) => {
        switch (filterType) {
            case 'sourceNumber':
                setSourceNumber(value);
                break;
            case 'destinationNumber':
                setDestinationNumber(value);
                break;
        }
        setCurrentPage(1);
        // Auto-fetch when text filter changes
        setTimeout(() => fetchData(), 100);
    };

    const handleRowClick = (e, rowData) => {
        // Don't trigger row click if clicking on a button or link
        if (e.target.tagName.toLowerCase() === 'button' || 
            e.target.tagName.toLowerCase() === 'a' ||
            e.target.closest('button') ||
            e.target.closest('a')) {
            return;
        }
        e.preventDefault();
        
        // Get the primary key from the row data
        const pk = rowData.ATRMESSAGEID;
        if (pk) {
            setDrawerId(pk);
            setDrawerIsOpen(true);
        }
    };

    const handleDrawerClose = () => {
        setDrawerIsOpen(false);
        setDrawerId(null);
    };

    // CSV Export functions
    const handleCsvExport = async () => {
        setCsvExportLoading(true);
        setCsvExportStatus('initiating');
        
        try {
            // Prepare export parameters based on current filters
            const exportParams = {
                start_time_from: d1.toISOString(),
                start_time_to: d2.toISOString(),
                client_id: selectedClient,
                originating_carrier_id: selectedOriginatingCarrier,
                terminating_carrier_id: selectedTerminatingCarrier,
                destination: selectedDestination,
                direction: selectedDirection,
                src_address: sourceNumber,
                dest_address: destinationNumber
            };

            const response = await create('/admin/reports/sms/smslog/export', { data: exportParams });
            
            if (response.data?.result === 'OK') {
                const taskId = response.data.task_id;
                setCsvExportTaskId(taskId);
                setCsvExportStatus('processing');
                
                toast.success('CSV export started. You will be notified when it\'s ready.', {
                    autoClose: 5000
                });
                
                // Start polling for progress
                pollExportProgress(taskId);
            } else {
                throw new Error(response.data?.message || 'Failed to start CSV export');
            }
        } catch (error) {
            console.error('CSV Export Error:', error);
            toast.error('Failed to start CSV export: ' + (error.response?.data?.message || error.message));
            setCsvExportStatus(null);
        } finally {
            setCsvExportLoading(false);
        }
    };

    const handleCsvDownload = () => {
        if (csvDownloadUrl) {
            // Create a temporary link element and trigger download
            const link = document.createElement('a');
            // Use the same base URL construction as in details.jsx
            link.href = `${config.usageReport.basePath}${csvDownloadUrl}`;
            link.download = `sms_log_export_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Reset the download state after a delay
            setTimeout(() => {
                setCsvExportStatus(null);
                setCsvExportTaskId(null);
                setCsvExportProgress(0);
                setCsvDownloadUrl(null);
            }, 5000);
        }
    };

    const pollExportProgress = async (taskId) => {
        const pollInterval = setInterval(async () => {
            try {
                const response = await getList(`/admin/reports/sms/smslog/export/status/${taskId}`);
                
                if (response.data?.result === 'OK') {
                    const status = response.data.status;
                    const progress = response.data.progress || 0;
                    
                    setCsvExportProgress(progress);
                    
                    if (status === 'completed') {
                        clearInterval(pollInterval);
                        setCsvExportStatus('completed');
                        setCsvExportProgress(100);
                        
                        // Store download URL for the download button (relative path only)
                        const downloadUrl = response.data.download_url;
                        if (downloadUrl) {
                            // Extract relative path from the full URL (remove base URL if present)
                            const relativePath = downloadUrl.replace(/^https?:\/\/[^\/]+/, '');
                            setCsvDownloadUrl(relativePath);
                            toast.success('CSV export completed!', {
                                autoClose: 3000
                            });
                        }
                        
                    } else if (status === 'failed') {
                        clearInterval(pollInterval);
                        setCsvExportStatus('failed');
                        toast.error('CSV export failed: ' + (response.data.error || 'Unknown error'));
                        
                        // Reset state after a delay
                        setTimeout(() => {
                            setCsvExportStatus(null);
                            setCsvExportTaskId(null);
                            setCsvExportProgress(0);
                            setCsvDownloadUrl(null);
                        }, 10000);
                    }
                }
            } catch (error) {
                console.error('Error polling export progress:', error);
                // Continue polling unless it's a 404 (task not found)
                if (error.response?.status === 404) {
                    clearInterval(pollInterval);
                    setCsvExportStatus('failed');
                    toast.error('Export task not found');
                }
            }
        }, 2000); // Poll every 2 seconds
        
        // Clear interval after 30 minutes to prevent infinite polling
        setTimeout(() => {
            clearInterval(pollInterval);
            if (csvExportStatus === 'processing') {
                setCsvExportStatus('timeout');
                toast.warning('CSV export is taking longer than expected. Please check back later.');
            }
        }, 30 * 60 * 1000);
    };

    const metadata = [
        {
            label: 'id',
            source: 'ATRMESSAGEID',
            type: 'primary',
            visible: false
        },
        {
            label: 'Timestamp',
            source: 'ATRTIMESTAMP',
            type: 'datetime',
        },
        {
            label: 'Type',
            source: 'ATRMSGTYPE',
            type: 'icon',
            values : {
                sms: 'SMS',
                mms: 'MMS'
            },
            classnames : {
                mms: 'label label-info',
                sms: 'label label-warning'
            }
        },
        {
            label: 'Direction',
            source: 'ATRDIRECTION',
            type: 'icon',
            values : {
                in: 'MO',
                out: 'MT'
            },
            classnames : {
                in: 'label label-info',
                out: 'label label-warning'
            }
        },
        {
            label: 'Origination',
            source: 'origination',
            type: 'text'
        },
        {
            label: 'Termination',
            source: 'termination',
            type: 'text'
        },
        {
            label: 'Dest. Address',
            source: 'ATRDESTADDR',
            type: 'text'
        },
        {
            label: 'Src. Address',
            source: 'ATRSRCADDR',
            type: 'text'
        },
        {
            label: 'Dest. Network',
            source: 'ATRCLIENTDESTNAME',
            type: 'text'
        },
        {
            label: 'SMSC Stat',
            source: 'smsc_response_command_status',
            type: 'icon',
            values : {
                'OK': 'OK',
                'ERROR': 'ERROR'
            },
            classnames : {
                'OK': 'label label-success',
                'ERROR': 'label label-danger'
            }
        },
        {
            label: 'DLR Status',
            source: 'dlr_status',
            type: 'icon',
            values : {
                'OK': 'OK',
                'DELIVRD': 'DELIVRD',
                'EXPIRED': 'EXPIRED',
                'REJECTD': 'REJECTD',
                'UNKNOWN': 'UNKNOWN',
                'UNDELIV': 'UNDELIV',
                'ERROR': 'ERROR'
            },
            classnames : {
                'OK': 'label label-success',
                'DELIVRD': 'label label-success',
                'EXPIRED': 'label label-warning',
                'REJECTD': 'label label-danger',
                'UNKNOWN': 'label label-warning',
                'UNDELIV': 'label label-danger',
                'ERROR': 'label label-danger'
            }
        },
        {
            label: 'Billed Amount',
            source: 'ATRCLIENTAMOUNT',
            type: 'money4'
        },
        {
            label: 'Cur',
            source: 'sell_currency_name',
            type: 'text'
        },
        {
            label: 'Cost',
            source: 'buy_amount',
            type: 'money4'
        },
        {
            label: 'Cur',
            source: 'buy_currency_name',
            type: 'text'
        },
        {
            label: 'Profit',
            source: 'profit',
            type: 'money4'
        },
        {
            label: 'Cur',
            source: 'profit_currency_name',
            type: 'text'
        },
        {
            label: 'Msg Parts',
            source: 'ATRNUMPARTS',
            type: 'text'
        },
        {
            label: 'Msg Size',
            source: 'ATRMSGSIZE',
            type: 'text'
        },
        {
            source: 'server_name',
            type: 'text',
            label: 'Server'
        }
    ];

    return (
        <Panel title="Global SMS Log">
            <>
                <div className="row">
                    <div className="col-md-12 form-group">
                        <label className="col-sm-1 control-label">From</label>
                        <div className="col-sm-3">
                            <DatePicker
                                className="form-control datepicker"
                                selected={d1}
                                onChange={handleDateRangeFromChange}
                                showTimeSelect
                                timeFormat="HH:mm"
                                dateFormat="yyyy-MM-dd HH:mm"
                            />
                        </div>
                        <label className="col-sm-1 control-label">To</label>
                        <div className="col-sm-3">
                            <DatePicker
                                className="form-control datepicker"
                                selected={d2}
                                onChange={handleDateRangeToChange}
                                showTimeSelect
                                timeFormat="HH:mm"
                                dateFormat="yyyy-MM-dd HH:mm"
                            />
                        </div>
                        <div className="col-sm-1">
                            <button className="btn btn-primary" onClick={handleSearch}>Search</button>
                        </div>
                        <div className="col-sm-3">
                            <div className="btn-group" role="group">
                                <button 
                                    className="btn btn-success btn-icon" 
                                    onClick={handleCsvExport}
                                    disabled={csvExportLoading || csvExportStatus === 'processing'}
                                    title="Export current filtered results to CSV"
                                >
                                    <i className="icon-download"></i> 
                                    {csvExportLoading ? 'Starting...' : 
                                     csvExportStatus === 'processing' ? `Exporting... (${csvExportProgress}%)` : 
                                     'Export CSV'}
                                </button>
                                
                                {csvExportStatus === 'completed' && csvDownloadUrl && (
                                    <button 
                                        className="btn btn-primary btn-icon" 
                                        onClick={handleCsvDownload}
                                        title="Download completed CSV export"
                                    >
                                        <i className="icon-download"></i> Download
                                    </button>
                                )}
                            </div>
                            
                            {csvExportStatus === 'processing' && (
                                <div className="progress" style={{marginTop: '5px', height: '4px'}}>
                                    <div 
                                        className="progress-bar progress-bar-striped progress-bar-animated" 
                                        role="progressbar" 
                                        style={{width: `${csvExportProgress}%`}}
                                        aria-valuenow={csvExportProgress} 
                                        aria-valuemin="0" 
                                        aria-valuemax="100"
                                    ></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Filters Row */}
                <div className="row" style={{marginBottom: '15px'}}>
                    <div className="col-md-2">
                        <label className="control-label">Client</label>
                        <AsyncSelectContainer
                            value={selectedClient}
                            onChange={(id, value) => handleFilterChange('client', value)}
                            loadOptions={loadClients}
                            placeholder="Type to search clients..."
                            isClearable={true}
                            defaultOptions={true}
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="control-label">Originating Carrier</label>
                        <SelectContainer
                            value={selectedOriginatingCarrier}
                            onChange={(id, value) => handleFilterChange('originatingCarrier', value)}
                            options={originatingCarrierOptions}
                            placeholder="All Carriers"
                            allowNull={true}
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="control-label">Terminating Carrier</label>
                        <SelectContainer
                            value={selectedTerminatingCarrier}
                            onChange={(id, value) => handleFilterChange('terminatingCarrier', value)}
                            options={terminatingCarrierOptions}
                            placeholder="All Carriers"
                            allowNull={true}
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="control-label">Destination</label>
                        <SelectContainer
                            value={selectedDestination}
                            onChange={(id, value) => handleFilterChange('destination', value)}
                            options={destinationOptions}
                            placeholder="All Destinations"
                            allowNull={true}
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="control-label">Direction</label>
                        <SelectContainer
                            value={selectedDirection}
                            onChange={(id, value) => handleFilterChange('direction', value)}
                            options={[
                                {value: null, label: 'All'},
                                {value: 'out', label: 'MT'},
                                {value: 'in', label: 'MO'}
                            ]}
                            placeholder="All Directions"
                            allowNull={true}
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="control-label">Source Number</label>
                        <input
                            type="text"
                            className="form-control"
                            value={sourceNumber}
                            onChange={(e) => handleTextFilterChange('sourceNumber', e.target.value)}
                            placeholder="Source Number"
                        />
                    </div>
                </div>

                <div className="row" style={{marginBottom: '15px'}}>
                    <div className="col-md-2">
                        <label className="control-label">Destination Number</label>
                        <input
                            type="text"
                            className="form-control"
                            value={destinationNumber}
                            onChange={(e) => handleTextFilterChange('destinationNumber', e.target.value)}
                            placeholder="Destination Number"
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <Pagination
                            page={currentPage}
                            total={totalRows}
                            onPageChange={handlePageChange}
                            rowsPerPage={rowsPerPage}
                            loading={loading}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <table id="sms-log-table" className="table table-bordered table-striped">
                            <thead>
                                <tr>
                                    {metadata.map((col, index) => (
                                        col.visible !== false && (
                                            <th key={index}>{col.label}</th>
                                        )
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={metadata.length} style={{textAlign: 'center'}}>Loading...</td>
                                    </tr>
                                ) : (
                                    data?.map((row, index) => (
                                        <tr 
                                            key={index} 
                                            onClick={(e) => handleRowClick(e, row)}
                                            className="cursor-pointer"
                                            style={{cursor: 'pointer'}}
                                        >
                                            {metadata.map((col, colIndex) => (
                                                col.visible !== false && (
                                                    col.type === 'money2' ? (
                                                        <td key={colIndex} style={{textAlign: 'right', paddingRight: '20px'}}>
                                                            {parseFloat(row[col.source] || 0).toFixed(2)}
                                                        </td>
                                                    ) : col.type === 'money4' ? (
                                                        <td key={colIndex} style={{textAlign: 'right', paddingRight: '20px'}}>
                                                            {parseFloat(row[col.source] || 0).toFixed(4)}
                                                        </td>
                                                    ) : col.type === 'datetime' ? (
                                                        <td key={colIndex} style={{textAlign: 'center', paddingRight: '20px'}}>
                                                            {row[col.source] ? new Date(row[col.source]).toLocaleString() : ''}
                                                        </td>
                                                    ) : col.type === 'icon' ? (
                                                        <td key={colIndex} style={{textAlign: 'center', paddingRight: '20px'}}>
                                                            <span className={col.classnames?.[row[col.source]] || ''}>
                                                                {col.values?.[row[col.source]] || row[col.source]}
                                                            </span>
                                                        </td>
                                                    ) : (
                                                        <td key={colIndex} style={{textAlign: 'center', paddingRight: '20px'}}>
                                                            {row[col.source]}
                                                        </td>
                                                    )
                                                )
                                            ))}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {totalRows > 0 && (
                    <div className="row">
                        <div className="col-md-12">
                            <Pagination
                                page={currentPage}
                                total={totalRows}
                                onPageChange={handlePageChange}
                                rowsPerPage={rowsPerPage}
                                loading={loading}
                            />
                        </div>
                    </div>
                )}

                {drawerIsOpen && drawerId && (
                    <DrawerPane
                        open={drawerIsOpen}
                        onClose={handleDrawerClose}
                        url="/admin/reports/sms/smslog/"
                        id={drawerId}
                    >
                        <SmsLogDetails />
                    </DrawerPane>
                )}

                <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                />
            </>
        </Panel>
    );
};

export default SmsLog;